export interface CreateSpectrogramPngOptions {
  audioBuffer: AudioBuffer;
  width?: number;
  height?: number;
}

const FFT_SIZE = 1024;

function getMonoChannel(audioBuffer: AudioBuffer): Float32Array {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }

  const mono = new Float32Array(audioBuffer.length);

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
    const data = audioBuffer.getChannelData(channel);

    for (let index = 0; index < mono.length; index += 1) {
      mono[index] += data[index] / audioBuffer.numberOfChannels;
    }
  }

  return mono;
}

function fftRadix2(real: Float32Array, imaginary: Float32Array): void {
  const n = real.length;
  let j = 0;

  for (let i = 1; i < n; i += 1) {
    let bit = n >> 1;

    while (j & bit) {
      j ^= bit;
      bit >>= 1;
    }

    j ^= bit;

    if (i < j) {
      const tempReal = real[i];
      const tempImaginary = imaginary[i];

      real[i] = real[j];
      imaginary[i] = imaginary[j];
      real[j] = tempReal;
      imaginary[j] = tempImaginary;
    }
  }

  for (let length = 2; length <= n; length <<= 1) {
    const angle = (-2 * Math.PI) / length;
    const wLengthReal = Math.cos(angle);
    const wLengthImaginary = Math.sin(angle);

    for (let i = 0; i < n; i += length) {
      let wReal = 1;
      let wImaginary = 0;

      for (let k = 0; k < length / 2; k += 1) {
        const evenIndex = i + k;
        const oddIndex = evenIndex + length / 2;
        const oddReal = real[oddIndex] * wReal - imaginary[oddIndex] * wImaginary;
        const oddImaginary = real[oddIndex] * wImaginary + imaginary[oddIndex] * wReal;

        real[oddIndex] = real[evenIndex] - oddReal;
        imaginary[oddIndex] = imaginary[evenIndex] - oddImaginary;
        real[evenIndex] += oddReal;
        imaginary[evenIndex] += oddImaginary;

        const nextWReal = wReal * wLengthReal - wImaginary * wLengthImaginary;
        wImaginary = wReal * wLengthImaginary + wImaginary * wLengthReal;
        wReal = nextWReal;
      }
    }
  }
}

function energyToColor(energy: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, energy));
  const red = Math.round(22 + clamped * 232);
  const green = Math.round(36 + Math.sin(clamped * Math.PI) * 154 + clamped * 54);
  const blue = Math.round(48 + (1 - clamped) * 42);

  return [red, green, blue];
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error("Canvas failed to create a PNG blob."));
    }, "image/png");
  });
}

export async function createSpectrogramPngBlob(options: CreateSpectrogramPngOptions): Promise<Blob> {
  if (typeof document === "undefined") {
    throw new Error("Spectrogram export requires a browser canvas.");
  }

  const width = options.width ?? 720;
  const height = options.height ?? 320;
  const data = getMonoChannel(options.audioBuffer);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas 2D context is unavailable.");
  }

  canvas.width = width;
  canvas.height = height;

  const real = new Float32Array(FFT_SIZE);
  const imaginary = new Float32Array(FFT_SIZE);
  const magnitudes = new Float32Array(width * height);
  const maxStart = Math.max(0, data.length - FFT_SIZE);
  let maxMagnitude = 0;

  for (let x = 0; x < width; x += 1) {
    const start = Math.floor((x / Math.max(1, width - 1)) * maxStart);

    imaginary.fill(0);

    for (let index = 0; index < FFT_SIZE; index += 1) {
      const sourceIndex = Math.min(data.length - 1, start + index);
      const window = 0.5 * (1 - Math.cos((2 * Math.PI * index) / (FFT_SIZE - 1)));

      real[index] = (data[sourceIndex] ?? 0) * window;
    }

    fftRadix2(real, imaginary);

    for (let y = 0; y < height; y += 1) {
      const frequency01 = 1 - y / Math.max(1, height - 1);
      const bin = Math.min(FFT_SIZE / 2 - 1, Math.max(1, Math.floor(frequency01 ** 1.7 * (FFT_SIZE / 2 - 1))));
      const magnitude = Math.sqrt(real[bin] * real[bin] + imaginary[bin] * imaginary[bin]);
      const outputIndex = y * width + x;

      magnitudes[outputIndex] = magnitude;
      maxMagnitude = Math.max(maxMagnitude, magnitude);
    }
  }

  const image = context.createImageData(width, height);
  const normalizer = maxMagnitude > 0 ? maxMagnitude : 1;

  for (let pixel = 0; pixel < magnitudes.length; pixel += 1) {
    const energy = Math.log10(1 + (magnitudes[pixel] / normalizer) * 24) / Math.log10(25);
    const [red, green, blue] = energyToColor(energy);
    const offset = pixel * 4;

    image.data[offset] = red;
    image.data[offset + 1] = green;
    image.data[offset + 2] = blue;
    image.data[offset + 3] = 255;
  }

  context.putImageData(image, 0, 0);
  context.fillStyle = "rgba(255,255,255,0.82)";
  context.font = "12px system-ui, sans-serif";
  context.fillText("time ->", 12, height - 12);
  context.save();
  context.translate(14, 84);
  context.rotate(-Math.PI / 2);
  context.fillText("frequency ->", 0, 0);
  context.restore();

  return canvasToBlob(canvas);
}
